import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  GridLegacy as Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Paper,
} from '@mui/material';
import {
  Notifications as NotificationIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Campaign as CampaignIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';

interface NotificationTemplate {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  targetAudience: 'all' | 'admins' | 'verified' | 'level-specific';
  targetLevel?: string;
  createdAt: any;
  createdBy: string;
  sentCount?: number;
}

interface NotificationHistory {
  id: string;
  title: string;
  message: string;
  type: string;
  sentTo: number;
  sentAt: any;
  sentBy: string;
}

export const AdminNotifications: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [history, setHistory] = useState<NotificationHistory[]>([]);
  const [createDialog, setCreateDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [adminUsers, setAdminUsers] = useState<any[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info' as 'info' | 'success' | 'warning' | 'error',
    targetAudience: 'all' as 'all' | 'admins' | 'verified' | 'level-specific',
    targetLevel: 'unranked' as string,
    adminTarget: 'all' as 'all' | 'specific',
    specificAdminId: '' as string,
    sendImmediately: true,
    saveAsTemplate: false,
  });

  // Preview state
  const [previewData, setPreviewData] = useState({
    title: '',
    message: '',
    type: 'info' as string,
    estimatedRecipients: 0,
  });

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!userDoc.exists() || !userDoc.data().isAdmin) {
          setError('Access denied. Admin privileges required.');
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }

        setIsAdmin(true);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setError('Failed to verify admin status.');
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [currentUser, navigate]);

  // Load templates and history
  useEffect(() => {
    const loadNotificationData = async () => {
      if (!isAdmin) return;

      try {
        // Load templates
        const templatesQuery = query(
          collection(db, 'notificationTemplates'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        const templatesSnapshot = await getDocs(templatesQuery);
        const templatesData: NotificationTemplate[] = [];
        templatesSnapshot.forEach((doc) => {
          const data = doc.data() as NotificationTemplate;
          data.id = doc.id;
          templatesData.push(data);
        });
        setTemplates(templatesData);

        // Load history
        const historyQuery = query(
          collection(db, 'notificationHistory'),
          orderBy('sentAt', 'desc'),
          limit(20)
        );
        const historySnapshot = await getDocs(historyQuery);
        const historyData: NotificationHistory[] = [];
        historySnapshot.forEach((doc) => {
          const data = doc.data() as NotificationHistory;
          data.id = doc.id;
          historyData.push(data);
        });
        setHistory(historyData);

        // Load admin users for testing
        const usersQuery = collection(db, 'users');
        const usersSnapshot = await getDocs(usersQuery);
        const admins: any[] = [];
        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          if (userData.isAdmin) {
            userData.uid = doc.id;
            admins.push(userData);
          }
        });
        setAdminUsers(admins);

      } catch (error) {
        console.error('Error loading notification data:', error);
        setError('Failed to load notification data.');
      }
    };

    loadNotificationData();
  }, [isAdmin]);

  const handleCreateNotification = async () => {
    if (!formData.title.trim() || !formData.message.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    try {
      setSending(true);

      // Get target users
      const targetUsers = await getTargetUsers();
      
      if (targetUsers.length === 0) {
        setError('No users match the selected criteria.');
        return;
      }

      // Save as template if requested
      if (formData.saveAsTemplate) {
        await addDoc(collection(db, 'notificationTemplates'), {
          title: formData.title,
          message: formData.message,
          type: formData.type,
          targetAudience: formData.targetAudience,
          targetLevel: formData.targetLevel,
          createdAt: serverTimestamp(),
          createdBy: currentUser?.uid,
          sentCount: 0,
        });
      }

      // Send notifications if immediate
      if (formData.sendImmediately) {
        await sendNotificationToUsers(targetUsers);
        
        // Record in history
        await addDoc(collection(db, 'notificationHistory'), {
          title: formData.title,
          message: formData.message,
          type: formData.type,
          sentTo: targetUsers.length,
          sentAt: serverTimestamp(),
          sentBy: currentUser?.uid,
        });

        const isTestMode = formData.targetAudience === 'admins' && formData.adminTarget === 'specific';
        setSuccess(`${isTestMode ? '📧 Test notification' : 'Notification'} sent to ${targetUsers.length} ${targetUsers.length === 1 ? 'user' : 'users'} successfully.`);
      } else {
        setSuccess('Notification template saved successfully.');
      }

      // Reset form
      setFormData({
        title: '',
        message: '',
        type: 'info',
        targetAudience: 'all',
        targetLevel: 'unranked',
        adminTarget: 'all',
        specificAdminId: '',
        sendImmediately: true,
        saveAsTemplate: false,
      });
      setCreateDialog(false);

    } catch (error) {
      console.error('Error creating notification:', error);
      setError('Failed to create notification.');
    } finally {
      setSending(false);
    }
  };

  const getTargetUsers = async () => {
    const usersQuery = collection(db, 'users');
    const usersSnapshot = await getDocs(usersQuery);
    const users: any[] = [];

    usersSnapshot.forEach((doc) => {
      const userData = doc.data();
      userData.uid = doc.id;

      // Filter based on target audience
      switch (formData.targetAudience) {
        case 'all':
          users.push(userData);
          break;
        case 'admins':
          if (userData.isAdmin) {
            // If specific admin is selected, only include that admin
            if (formData.adminTarget === 'specific' && formData.specificAdminId) {
              if (userData.uid === formData.specificAdminId) {
                users.push(userData);
              }
            } else if (formData.adminTarget === 'all') {
              // Include all admins
              users.push(userData);
            }
          }
          break;
        case 'verified':
          if (userData.xmAffiliationVerified) users.push(userData);
          break;
        case 'level-specific':
          if (userData.currentXmLevel === formData.targetLevel) users.push(userData);
          break;
      }
    });

    return users;
  };

  const sendNotificationToUsers = async (users: any[]) => {
    // In a real implementation, this would integrate with a push notification service
    // For now, we'll just create notification records in Firestore
    const batch = [];
    
    for (const user of users) {
      batch.push(
        addDoc(collection(db, 'userNotifications'), {
          userId: user.uid,
          title: formData.title,
          message: formData.message,
          type: formData.type,
          read: false,
          createdAt: serverTimestamp(),
        })
      );
    }

    await Promise.all(batch);
  };

  const handlePreviewNotification = async () => {
    try {
      const targetUsers = await getTargetUsers();
      setPreviewData({
        title: formData.title,
        message: formData.message,
        type: formData.type,
        estimatedRecipients: targetUsers.length,
      });
      setPreviewDialog(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      setError('Failed to generate preview.');
    }
  };

  const handleSendTemplate = async (template: NotificationTemplate) => {
    try {
      setSending(true);

      // Get target users
      const usersQuery = collection(db, 'users');
      const usersSnapshot = await getDocs(usersQuery);
      const users: any[] = [];

      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        userData.uid = doc.id;

        switch (template.targetAudience) {
          case 'all':
            users.push(userData);
            break;
          case 'admins':
            if (userData.isAdmin) users.push(userData);
            break;
          case 'verified':
            if (userData.xmAffiliationVerified) users.push(userData);
            break;
          case 'level-specific':
            if (userData.currentXmLevel === template.targetLevel) users.push(userData);
            break;
        }
      });

      // Send notifications
      const batch = [];
      for (const user of users) {
        batch.push(
          addDoc(collection(db, 'userNotifications'), {
            userId: user.uid,
            title: template.title,
            message: template.message,
            type: template.type,
            read: false,
            createdAt: serverTimestamp(),
          })
        );
      }

      await Promise.all(batch);

      // Update template sent count
      await updateDoc(doc(db, 'notificationTemplates', template.id), {
        sentCount: (template.sentCount || 0) + users.length,
      });

      // Record in history
      await addDoc(collection(db, 'notificationHistory'), {
        title: template.title,
        message: template.message,
        type: template.type,
        sentTo: users.length,
        sentAt: serverTimestamp(),
        sentBy: currentUser?.uid,
      });

      setSuccess(`Template sent to ${users.length} users successfully.`);

    } catch (error) {
      console.error('Error sending template:', error);
      setError('Failed to send template.');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      await deleteDoc(doc(db, 'notificationTemplates', templateId));
      setTemplates(templates.filter(t => t.id !== templateId));
      setSuccess('Template deleted successfully.');
    } catch (error) {
      console.error('Error deleting template:', error);
      setError('Failed to delete template.');
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'info';
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 4 }}>
            {error || 'Access denied. Admin privileges required.'}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <NotificationIcon sx={{ fontSize: 40 }} />
              Notifications Management
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Send notifications and manage communication with users
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<CampaignIcon />}
            onClick={() => setCreateDialog(true)}
            size="large"
          >
            Create Notification
          </Button>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 4 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Templates */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon />
                  Notification Templates
                </Typography>
                <List>
                  {templates.map((template) => (
                    <ListItem key={template.id} divider>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">{template.title}</Typography>
                            <Chip 
                              size="small" 
                              label={template.type} 
                              color={getTypeColor(template.type)} 
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {template.message.substring(0, 80)}...
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Target: {template.targetAudience} 
                              {template.targetLevel && ` (${template.targetLevel})`} • 
                              Sent: {template.sentCount || 0} times
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          size="small"
                          onClick={() => handleSendTemplate(template)}
                          disabled={sending}
                          sx={{ mr: 1 }}
                        >
                          <SendIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTemplate(template.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                  {templates.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="No templates yet"
                        secondary="Create your first notification template"
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent History */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon />
                  Recent Notifications
                </Typography>
                <List>
                  {history.map((item) => (
                    <ListItem key={item.id} divider>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">{item.title}</Typography>
                            <Chip 
                              size="small" 
                              label={item.type} 
                              color={getTypeColor(item.type)} 
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {item.message.substring(0, 80)}...
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sent to {item.sentTo} users • 
                              {item.sentAt?.toDate().toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                  {history.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="No notifications sent yet"
                        secondary="Notification history will appear here"
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Create Notification Dialog */}
        <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create New Notification</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Notification Title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                sx={{ mb: 2 }}
                required
              />
              
              <TextField
                fullWidth
                label="Message"
                multiline
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                sx={{ mb: 2 }}
                required
              />

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={formData.type}
                      label="Type"
                      onChange={(e) => setFormData({...formData, type: e.target.value as any})}
                    >
                      <MenuItem value="info">Info</MenuItem>
                      <MenuItem value="success">Success</MenuItem>
                      <MenuItem value="warning">Warning</MenuItem>
                      <MenuItem value="error">Error</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Target Audience</InputLabel>
                    <Select
                      value={formData.targetAudience}
                      label="Target Audience"
                      onChange={(e) => setFormData({...formData, targetAudience: e.target.value as any})}
                    >
                      <MenuItem value="all">All Users</MenuItem>
                      <MenuItem value="admins">Admins Only</MenuItem>
                      <MenuItem value="verified">Verified Users</MenuItem>
                      <MenuItem value="level-specific">Specific Level</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {formData.targetAudience === 'admins' && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Admin Target</InputLabel>
                      <Select
                        value={formData.adminTarget}
                        label="Admin Target"
                        onChange={(e) => setFormData({...formData, adminTarget: e.target.value as any, specificAdminId: ''})}
                      >
                        <MenuItem value="all">All Admins</MenuItem>
                        <MenuItem value="specific">Specific Admin (Test)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {formData.adminTarget === 'specific' && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Select Admin</InputLabel>
                        <Select
                          value={formData.specificAdminId}
                          label="Select Admin"
                          onChange={(e) => setFormData({...formData, specificAdminId: e.target.value})}
                        >
                          {adminUsers.map((admin) => (
                            <MenuItem key={admin.uid} value={admin.uid}>
                              {admin.displayName || admin.email || `Admin ${admin.uid.substring(0, 8)}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              )}

              {formData.targetAudience === 'level-specific' && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Target Level</InputLabel>
                  <Select
                    value={formData.targetLevel}
                    label="Target Level"
                    onChange={(e) => setFormData({...formData, targetLevel: e.target.value})}
                  >
                    <MenuItem value="unranked">Unranked</MenuItem>
                    <MenuItem value="bronze">Bronze</MenuItem>
                    <MenuItem value="silver">Silver</MenuItem>
                    <MenuItem value="gold">Gold</MenuItem>
                  </Select>
                </FormControl>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.sendImmediately}
                    onChange={(e) => setFormData({...formData, sendImmediately: e.target.checked})}
                  />
                }
                label="Send immediately"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.saveAsTemplate}
                    onChange={(e) => setFormData({...formData, saveAsTemplate: e.target.checked})}
                  />
                }
                label="Save as template"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={handlePreviewNotification} variant="outlined">
              Preview
            </Button>
            <Button 
              onClick={handleCreateNotification} 
              variant="contained"
              disabled={sending || !formData.title.trim() || !formData.message.trim() || 
                (formData.targetAudience === 'admins' && formData.adminTarget === 'specific' && !formData.specificAdminId)}
            >
              {sending ? <CircularProgress size={20} /> : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Notification Preview</DialogTitle>
          <DialogContent>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
              <Typography variant="h6" gutterBottom>
                {previewData.title}
              </Typography>
              <Typography variant="body2" paragraph>
                {previewData.message}
              </Typography>
              <Chip size="small" label={previewData.type} color={getTypeColor(previewData.type)} />
            </Paper>
            <Alert severity="info">
              This notification will be sent to <strong>{previewData.estimatedRecipients}</strong> users.
              {formData.targetAudience === 'admins' && formData.adminTarget === 'specific' && (
                <><br />📧 <strong>Test Mode:</strong> Sending to specific admin only.</>
              )}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}; 